//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import { Box, Grid, Tooltip, Typography, useTheme } from '@mui/material'
import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolBaseOverviewCTO,
  PoolDynamicAccountCTO,
  PoolFlexAccountCTO,
} from '@shared/sharedTypes'
import { useOtChainState, useOtSelector } from '@react/redux/store'

import Paper from '@mui/material/Paper'

import { erc20WeiToCommaDecString } from '@shared/conversions'

import { PoolTypeEnum } from '@shared/Enums'

import { EthAddr, ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'

export default function LenderPortfolioSummary(): React.ReactElement {
  const poolOverviewCTOs = useOtSelector((state) => state.poolState.poolOverviewCTOs)

  const {
    chainConfiguration: { perimeterAddresses },
    userConnection: { accountAddr },
  } = useOtChainState()

  const poolAccountCTOs = useOtSelector((state) => state.poolState.poolAccountCTOs)
  const liquidityAssets = perimeterAddresses?.liquidityAssets ?? []
  const theme = useTheme()
  const poolAddrs: EthAddr[] = Object.keys(poolOverviewCTOs)

  const [portfolioValues, setPortfolioValues] = useState<Record<string, bigint>>({})

  // useEffect(() => {
  //   poolAddrs.forEach((poolAddr: EthAddr) => {
  //     const poolBaseOverviewCTO = poolOverviewCTOs[poolAddr] as PoolBaseOverviewCTO
  //     const poolBaseAccountCTO =
  //       accountAddr && poolAddr ? (poolAccountCTOs?.[poolAddr]?.[accountAddr] as PoolBaseAccountCTO) : undefined

  //     if (poolBaseOverviewCTO.poolType == PoolTypeEnum.Term) {
  //       const poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
  //       if (!poolAccountCTO && accountAddr) {
  //         dispatch(poolActions.buildPoolAccountCTO({ poolAddr, accountAddr }))
  //       }
  //     } else {
  //       const poolAccountFlex = poolBaseAccountCTO as PoolFlexAccountCTO

  //       if (!poolAccountFlex && accountAddr) {
  //         dispatch(poolActions.buildPoolAccountCTO({ poolAddr, accountAddr }))
  //       }
  //     }
  //   })
  // }, [poolOverviewCTOs])

  useEffect(() => {
    if (liquidityAssets) {
      const newPV: Record<string, bigint> = {}
      liquidityAssets.forEach((liquidityAsset) => {
        const assetAddr = liquidityAsset.addr

        newPV[assetAddr] = 0n
      })

      poolAddrs.forEach((poolAddr: EthAddr) => {
        const poolBaseOverviewCTO = poolOverviewCTOs[poolAddr] as PoolBaseOverviewCTO

        const liquidityAssetAddr = poolBaseOverviewCTO.liquidityAssetAddr
        const poolBaseAccountCTO =
          accountAddr && poolAddr ? (poolAccountCTOs?.[poolAddr]?.[accountAddr] as PoolBaseAccountCTO) : undefined

        if (poolBaseAccountCTO) {
          switch (poolBaseOverviewCTO.poolType) {
            case PoolTypeEnum.Term: {
              const poolAccountCTO = poolBaseAccountCTO as PoolAccountCTO
              const dollars = BigInt(poolAccountCTO?.totalAssetCurrent ?? '0')
              const sum = BigInt(newPV[liquidityAssetAddr] ?? '0') + dollars
              newPV[liquidityAssetAddr] = sum
              break
            }
            case PoolTypeEnum.FlexibleRate: {
              const poolAccountFlex = poolBaseAccountCTO as PoolFlexAccountCTO
              const dollars = BigInt(poolAccountFlex?.accountOutstandingLoanPrincipals ?? '0')
              const sum = BigInt(newPV[liquidityAssetAddr] ?? '0') + dollars
              newPV[liquidityAssetAddr] = sum
              break
            }
            case PoolTypeEnum.Dynamic: {
              const poolAccountDynamic = poolBaseAccountCTO as PoolDynamicAccountCTO
              const dollars = BigInt(poolAccountDynamic?.principalEarningInterest ?? '0')
              const sum = BigInt(newPV[liquidityAssetAddr] ?? '0') + dollars
              newPV[liquidityAssetAddr] = sum
              break
            }
            default:
              logOT.warn('Unsupported pool type', poolBaseOverviewCTO.poolType)
              break
          }
        }
      })

      setPortfolioValues(newPV)
    }
  }, [perimeterAddresses, liquidityAssets, poolAccountCTOs, poolOverviewCTOs])

  return (
    <Paper
      key="paper1"
      data-testid="paper1"
      sx={{
        marginTop: 4,
        marginBottom: 4,
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Grid key="TopGrid" data-testid="TopGrid" container spacing={0} justifyContent="flex-start" direction={'row'}>
        <Grid
          key="TopGrid1"
          data-testid="TopGrid1"
          container
          item
          xs={6}
          spacing={0}
          justifyContent="flex-start"
          direction={'row'}
        >
          <Grid key="ToolTip1" data-testid="ToolTip1" item xs={6} sx={{ paddingBottom: 2, paddingTop: 1 }}>
            <Tooltip key="TT2" data-testid="TT2" title="The total value of all your investments if held to maturity.">
              <Typography key="Ty1" data-testid="Ty1" variant={'body'}>
                Portfolio Value
              </Typography>
            </Tooltip>
            {Object.keys(portfolioValues).map((key, index) => {
              const addr = key
              const val = portfolioValues[key]
              const textColor = '#0088DC'

              if (addr === ETH_ADDR_ZERO)
                return (
                  <Box
                    data-testid={`Box${index.toString()}`}
                    sx={{ textAlign: 'left' }}
                    key={`Box${index.toString()}`}
                  ></Box>
                )
              return (
                <Box data-testid={`Box${index.toString()}`} sx={{ textAlign: 'left' }} key={`Box${index.toString()}`}>
                  <Typography
                    data-testid={'Dollar' + index.toString()}
                    key={'dollar' + index.toString()}
                    display="inline"
                    align={'left'}
                    variant="h2Bold"
                  >
                    {erc20WeiToCommaDecString(val)}
                  </Typography>
                  <Typography
                    data-testid={'sign' + index.toString()}
                    key={'sign' + index.toString()}
                    color={textColor}
                    sx={{ marginLeft: 2 }}
                    display="inline"
                    align={'left'}
                    variant="h5Bold"
                  >
                    {liquidityAssets.find((asset) => asset.addr === addr)?.symbol}
                  </Typography>
                </Box>
              )
            })}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  )
}
